import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { SubmitError } from '@/app/components/SubmitError';
import { Button } from '@/app/components/ui/Button';
import { Checkbox, Field, FormActions, Input, Select, Textarea } from '@/app/components/ui/Field';
import { Modal } from '@/app/components/ui/Modal';
import { createChallenge } from '@/features/challenges/api';
import type { ChallengeMode } from '@/features/challenges/api';

const CHALLENGE_TYPES = ['steps', 'sleep', 'workouts', 'meditation', 'custom'] as const;

interface FormState {
  title: string;
  description: string;
  type: string;
  mode: ChallengeMode;
  startDate: string;
  endDate: string;
  goalValue: string;
  unit: string;
  reward: string;
  leaderboardVisible: boolean;
}

const EMPTY: FormState = {
  title: '',
  description: '',
  type: CHALLENGE_TYPES[0],
  mode: 'individual',
  startDate: '',
  endDate: '',
  goalValue: '',
  unit: '',
  reward: '',
  leaderboardVisible: true,
};

/**
 * The Challenges "new challenge" form (PLAN-FASE2.md WP-05), exactly the
 * flowchart's field set: Title, Description, Type (dropdown), Mode
 * (Individual/Team), Start/end date, Goal value, Unit, Reward, Leaderboard
 * toggle. Always creates a `draft` — publishing is a separate action on the
 * card (`PUT /challenges/{id}/status`).
 */
export function NewChallengeModal({
  open,
  companyId,
  onClose,
  onCreated,
}: {
  open: boolean;
  companyId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<Error | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function validate(): boolean {
    const errors: Partial<Record<keyof FormState, string>> = {};
    if (form.title.trim() === '') errors.title = t('challenges.form.errors.required');
    if (form.type.trim() === '') errors.type = t('challenges.form.errors.required');
    if (form.startDate === '') errors.startDate = t('challenges.form.errors.required');
    if (form.endDate === '') errors.endDate = t('challenges.form.errors.required');
    if (form.startDate !== '' && form.endDate !== '' && form.endDate < form.startDate) {
      errors.endDate = t('challenges.form.errors.endBeforeStart');
    }
    if (form.goalValue.trim() === '' || Number.isNaN(Number(form.goalValue))) {
      errors.goalValue = t('challenges.form.errors.required');
    }
    if (form.unit.trim() === '') errors.unit = t('challenges.form.errors.required');
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitError(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      await createChallenge(companyId, {
        title: form.title.trim(),
        description: form.description.trim() === '' ? null : form.description.trim(),
        type: form.type,
        mode: form.mode,
        start_date: form.startDate,
        end_date: form.endDate,
        goal_value: Number(form.goalValue),
        unit: form.unit.trim(),
        reward: form.reward.trim() === '' ? null : form.reward.trim(),
        leaderboard_visible: form.leaderboardVisible,
      });
      setForm(EMPTY);
      onCreated();
    } catch (cause) {
      setSubmitError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={t('challenges.form.title')}>
      <form className="flex flex-col gap-4" onSubmit={(event) => void handleSubmit(event)}>
        <Field label={t('challenges.form.fields.title')} error={fieldErrors.title}>
          <Input value={form.title} onChange={(e) => set('title', e.target.value)} />
        </Field>
        <Field label={t('challenges.form.fields.description')}>
          <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} />
        </Field>
        <Field label={t('challenges.form.fields.type')} error={fieldErrors.type}>
          <Select value={form.type} onChange={(e) => set('type', e.target.value)}>
            {CHALLENGE_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`challenges.type.${type}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('challenges.form.fields.mode')}>
          <Select value={form.mode} onChange={(e) => set('mode', e.target.value as ChallengeMode)}>
            <option value="individual">{t('challenges.mode.individual')}</option>
            <option value="group">{t('challenges.mode.group')}</option>
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('challenges.form.fields.startDate')} error={fieldErrors.startDate}>
            <Input
              type="date"
              value={form.startDate}
              onChange={(e) => set('startDate', e.target.value)}
            />
          </Field>
          <Field label={t('challenges.form.fields.endDate')} error={fieldErrors.endDate}>
            <Input
              type="date"
              value={form.endDate}
              onChange={(e) => set('endDate', e.target.value)}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('challenges.form.fields.goalValue')} error={fieldErrors.goalValue}>
            <Input
              type="number"
              value={form.goalValue}
              onChange={(e) => set('goalValue', e.target.value)}
            />
          </Field>
          <Field label={t('challenges.form.fields.unit')} error={fieldErrors.unit}>
            <Input value={form.unit} onChange={(e) => set('unit', e.target.value)} />
          </Field>
        </div>
        <Field label={t('challenges.form.fields.reward')}>
          <Input value={form.reward} onChange={(e) => set('reward', e.target.value)} />
        </Field>
        <Checkbox
          label={t('challenges.form.fields.leaderboardVisible')}
          checked={form.leaderboardVisible}
          onChange={(e) => set('leaderboardVisible', e.target.checked)}
        />
        <SubmitError error={submitError} />
        <FormActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('challenges.form.cancel')}
          </Button>
          <Button type="submit" disabled={submitting}>
            {t('challenges.form.submit')}
          </Button>
        </FormActions>
      </form>
    </Modal>
  );
}
